import { IDayAction } from "@components/calendar/Calendar.utils";
import { getIntentLink } from "@components/drillDown/DrillDown.utils";
import { parseTimeUserInput } from "@components/inputs/date";
import {
    formatMinutesToHoursString,
    formatMinutesToTimeFormat,
    formatTimeVariants,
    getHourDifference,
    getMinutesDiff,
    getMonthDays,
    isSameDay,
    isValidDate,
    longDateFormat
} from "@components/inputs/date/utils";
import Field from "@components/inputs/field";
import { IFieldDefFn, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { addEmptyLineItem } from "@components/smart/smartFastEntryList";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { getExpandedEnumProps, getNewItemsMaxId } from "@odata/Data.utils";
import {
    EntitySetName,
    EntityTypeName,
    IPrAttendanceDayEntity,
    IPrAttendanceDayIntervalEntity,
    IPrAttendanceEntity,
    IPrEmploymentTemporalEntity,
    IPrPayrollSettingEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrAttendanceDayEntity,
    PrAttendanceDayIntervalEntity,
    PrAttendanceEntity,
    PrPayrollSettingEntity,
    PrPayrollSettingTemporalCurrentEntity,
    PrPayrollSettingTemporalEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    PrAbsenceCategoryTypeCode,
    PrBenefitTypeCode,
    PrEmploymentTypeCode,
    PrHolidayShiftAssignmentCode,
    PrWorkingPatternTypeCode,
    PrWorkIntervalTypeCode
} from "@odata/GeneratedEnums";
import { DayWorkStartPath, IsFullDayAbsencePath } from "@pages/payroll/attendance/Attendance.def";
import { AttendanceFastEntry, IntervalType, TDayMinutesMap } from "@pages/payroll/attendance/Attendance.types";
import { createDateFromPeriod } from "@pages/payroll/payroll.utils";
import { IFormTableListRow, IFormTableListSection } from "@pages/payroll/payslip/formTableList/FormTableList";
import { getEntitySetFromType } from "@pages/payroll/socialEvents/SocialEvents.utils";
import { isDefined, isNotDefined, isObjectEmpty, roundToDecimalPlaces, sortCompareFn } from "@utils/general";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import { BREAK_LENGTH, DASH_CHARACTER, REST_API_URL, WORK_LENGTH } from "../../../constants";
import { Sort } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { ROUTE_SOCIAL_EVENTS } from "../../../routes";
import DateType, { getDateFormat, getUtcDayjs } from "../../../types/Date";
import customFetch from "../../../utils/customFetch";
import memoize from "../../../utils/memoize";
import { FormStorage } from "../../../views/formView/FormStorage";
import { timeDiffPath } from "../workingPatterns/WorkingPatternsDef";
import { IAttendanceCustomData } from "./AttendanceFormView";

export const ATTENDANCE_FAKE_ID = -9999;
export const DPP_YEARLY_LIMIT_IN_MINUTES = 300 * 60;
const CLEAR_ATTENDANCE_URL = `${REST_API_URL}/PrAttendanceGenerator/Regenerate/`;

export function isValidInterval(interval: IPrAttendanceDayIntervalEntity): boolean {
    return (!isObjectEmpty(interval.AbsenceCategory) || !isObjectEmpty(interval.WorkType))
        && isValidDate(interval.TimeStart) && isValidDate(interval.TimeEnd);
}

export function getHoursFromTimeStamp(timeStamp: Date): number {
    if (isObjectEmpty(timeStamp)) {
        return null;
    }
    return getUtcDayjs(timeStamp).get("hours") + roundToDecimalPlaces(3, getUtcDayjs(timeStamp).get("minutes") / 60);
}

function getIntervalMinutes(day: IPrAttendanceDayEntity, interval: IPrAttendanceDayIntervalEntity): number {
    if (!isValidInterval(interval)) {
        return 0;
    }

    if (!!interval.AbsenceCategory?.Id) {
        if (interval.AbsenceCategory.TypeCode === PrAbsenceCategoryTypeCode.NáhradníVolnoZaPřesčas) {
            // todo: check if this is correct
            return 0;
        }

        if (interval.IsWholeDay) {
            return day.Planned ?? 0;
        }
    }

    return getMinutesDiff(getUtcDayjs(interval.TimeStart), getUtcDayjs(interval.TimeEnd));
}

export function getDayIntervalsMinutes(day: IPrAttendanceDayEntity, type: IntervalType): number {
    if (!day?.Intervals?.length) {
        return 0;
    }

    return getFilteredAbsencesForDay(day.Intervals, day)
        .reduce((totalMinutes, interval) => {
        if (interval.IsPlaceholder || !isValidInterval(interval)) {
            // placeholders are social events that are not yet confirmed,
            // and most likely should not affect the hours calculation anywhere in attendance
            return totalMinutes;
        }
        const isWork = interval.WorkType?.Code === PrWorkIntervalTypeCode.Work;
        const isAbsence = !!interval.AbsenceCategory?.Id;
        const isHolidayCompensation = interval.WorkType?.Code === PrWorkIntervalTypeCode.CompensationForHoliday;

        if (
            (type === IntervalType.Work && isWork) ||
            (type === IntervalType.Absence && isAbsence) ||
            (type === IntervalType.All && (isWork || isAbsence || isHolidayCompensation))
        ) {
            return totalMinutes + getIntervalMinutes(day, interval);
        }
        return totalMinutes;
    }, 0);
}

export function getWorkingPattern(attendance: IPrAttendanceEntity): IPrWorkingPatternEntity | null {
    const monthDate = createDateFromPeriod(attendance);

    function findFn(bag: IPrEmploymentTemporalEntity) {
        return getUtcDayjs(monthDate).isBetween(getUtcDayjs(bag.DateValidFrom), getUtcDayjs(bag.DateValidTo), "day", "[]");
    }

    return attendance.Employment?.TemporalPropertyBag?.find(findFn)?.WorkingPattern;
}

function getWeeklyPatternDaysMap(
        workingPattern: IPrWorkingPatternEntity,
        monthDays: Dayjs[],
        holidays: Date[]
): TDayMinutesMap {
    const result = monthDays.reduce((map: TDayMinutesMap, day) => {
        const dayIndex = day.get("date");
        const wpDay = workingPattern.Days?.find(d => {
            let res = day.day() === getUtcDayjs(d.Date).day();
            if (workingPattern.IsDifferentOddAndEvenWeek) {
                res = res && (day.week() % 2 === getUtcDayjs(d.Date).week() % 2);
            }
            return res;
        });
        map[dayIndex] = getHoursFromTimeStamp(wpDay?.WorkingHours) ?? 0;
        return map;
    }, {});

    for (const holiday of holidays) {
        result[getUtcDayjs(holiday).get("date")] = 0;
    }
    return result;
}

function getRotationPatternDaysMap(
        workingPattern: IPrWorkingPatternEntity,
        monthDays: Dayjs[]
): TDayMinutesMap {
    if (!workingPattern.Days?.length) {
        return getDefaultPatternDaysMap(monthDays);
    }
    const sortedDays = workingPattern.Days.sort((a, b) => sortCompareFn(a.Date, b.Date, Sort.Asc));
    return monthDays.reduce((map: TDayMinutesMap, day) => {
        const dayIndex = day.get("date");
        const rotationStart = getUtcDayjs(sortedDays[0].Date);
        const rotationLength = workingPattern.Days.length;
        const daysSinceRotationStart = day.diff(rotationStart, "days");
        const rotationDayIndex = ((daysSinceRotationStart % rotationLength) + rotationLength) % rotationLength;
        const wpDay = sortedDays[rotationDayIndex];
        map[dayIndex] = getHoursFromTimeStamp(wpDay?.WorkingHours) ?? 0;
        return map;
    }, {});
}

function getDefaultPatternDaysMap(monthDays: Dayjs[]): Record<number, number> {
    return monthDays.reduce((map: Record<number, number>, day) => {
        const dayIndex = day.get("date");
        map[dayIndex] = 0;
        return map;
    }, {});
}

export function getWorkingPatternDaysMap(
        workingPattern: IPrWorkingPatternEntity,
        month: Dayjs,
        holidays: Date[] = []
): TDayMinutesMap {
    const monthDays = getMonthDays(month);
    if (workingPattern.Type?.Code === PrWorkingPatternTypeCode.Weekly) {
        return getWeeklyPatternDaysMap(workingPattern, monthDays, holidays);
    }
    if (workingPattern.Type?.Code === PrWorkingPatternTypeCode.Rotation) {
        return getRotationPatternDaysMap(workingPattern, monthDays);
    }
    return getDefaultPatternDaysMap(monthDays);
}

export const balanceFormatter: TFormatterFn = (val: TValue, args) => {
    const attendance: IPrAttendanceEntity = args?.entity ?? args?.storage.data.entity;
    const numVal = Number(val);

    if (attendance?.IsStatutoryCompensation || isNaN(numVal)) {
        return DASH_CHARACTER;
    }

    const sign = numVal > 0 ? "+" : numVal < 0 ? "-" : "";
    const color = numVal > 0 ? "C_SEM_text_good" : numVal < 0 ? "C_SEM_text_bad" : (args.customArgs?.color as string ?? "C_TEXT_primary");

    const formattedVal = `${sign}${formatMinutesToTimeFormat(Math.abs(val as number))} ${args?.storage.t("Components:Calendar.HourPlaceholder")}`;
    return {
        value: (
            <ColoredText color={color}>{formattedVal}</ColoredText>
        ),
        tooltip: formattedVal
    };
};

export function getTopPriorityDayAbsence(day: IPrAttendanceDayEntity, isPlaceholder = false): IPrAttendanceDayIntervalEntity | null {
    const absences = day.Intervals?.filter(i => !!i.AbsenceCategory?.Id && !!i.IsPlaceholder === isPlaceholder);
    if (!absences?.length) {
        return null;
    }

    let highestPriority = Number.MAX_SAFE_INTEGER; // higher the number, lower the priority
    return absences.reduce((res: IPrAttendanceDayIntervalEntity, a: IPrAttendanceDayIntervalEntity) => {
        if (a.AbsenceCategory?.Type?.Priority < highestPriority) {
            highestPriority = a.AbsenceCategory?.Type?.Priority;
            res = a;
        }
        return res;
    }, null);
}

function getDayActionFromAbsence(absence: IPrAttendanceDayIntervalEntity): Pick<IDayAction, "color" | "title" | "isFullDay" | "categoryId"> | null {
    if (isObjectEmpty(absence?.AbsenceCategory)) {
        return null;
    }

    return {
        color: absence.AbsenceCategory.Color as keyof DefaultTheme,
        title: absence.AbsenceCategory.CurrentTemporalPropertyBag?.Name,
        isFullDay: absence.IsWholeDay,
        categoryId: absence.AbsenceCategory.Id
    };
}

export const parseDayActionsFromData = (days: IPrAttendanceDayEntity[]): Record<number, IDayAction[]> => {
    return [...days]
        .sort((d1, d2) => sortCompareFn(d1.Date, d2.Date))
        .reduce((actionsMap: Record<number, IDayAction[]>, day) => {
            const dayjs = getUtcDayjs(day.Date);
            const dayIndex = dayjs.get("date");
            const isMonday = dayjs.day() === 1;
            actionsMap[dayIndex] = [];

            // placeholders are social events that are not yet confirmed, absences are social events that are confirmed
            // (with link to social event) or other absences (like sick leave, vacation, etc.) filled in form
            // show only the highest priority placeholder for the day, if any as placeholder are always whole day absences
            if (getPlaceholders(day)?.length) {
                const paramountPlaceholder = getTopPriorityDayAbsence(day, true);
                const lastPlaceholderCategoryId = actionsMap[dayIndex - 1]?.find(i => !!i.isPlaceholder)?.categoryId;
                const isContinuing = lastPlaceholderCategoryId === paramountPlaceholder?.AbsenceCategory?.Id;

                actionsMap[dayIndex].push({
                    ...getDayActionFromAbsence(paramountPlaceholder),
                    hideTitle: isContinuing,
                    isStart: !isContinuing,
                    isEnd: true,
                    isPlaceholder: true,
                });

                if (isContinuing && actionsMap[dayIndex - 1]) {
                    actionsMap[dayIndex - 1].find(i => !!i.isPlaceholder && i.categoryId === paramountPlaceholder.AbsenceCategory.Id).isEnd = isMonday;
                }
            }

            // placeholders are all set, now look at absences, if there is some whole day absence linked to social event, we will show only the highest priority one,
            // if there are multiple absences on the same day and at least one of them is a whole day (and is not link to social event) absence or they overlap,
            // we will show a single action with conflict, otherwise we will show them all
            const absences = getAbsenceIntervals(day);
            if (absences?.length) {
                // we care about last day absence only if it's the only one that day, as we have to know if it's the start of absence or not
                let lastAbsenceId = null;
                if (actionsMap[dayIndex - 1]?.filter(i => !i.isPlaceholder && !i.hasConflict).length === 1) {
                    lastAbsenceId = actionsMap[dayIndex - 1].find(i => !i.isPlaceholder && !i.hasConflict).categoryId;
                }
                
                function someAbsencesOverlap(absences: IPrAttendanceDayIntervalEntity[]) {
                    if (!Array.isArray(absences) || absences.length <= 1) {
                        return false;
                    }
                    const MINUTES_IN_DAY = 24 * 60;

                    // 1. Convert each absence into absolute minutes from a shared baseline
                    const intervals = absences.map(absence => {
                        const startjs = getUtcDayjs(absence.TimeStart);
                        const endjs = getUtcDayjs(absence.TimeEnd);
                        let start: number = startjs.get("h") * 60 + startjs.get("m");
                        let end: number = endjs.get("h") * 60 + endjs.get("m");

                        if (absence.IsStartNextDay) {
                            start += MINUTES_IN_DAY;
                        }
                        if (absence.IsEndNextDay) {
                            end += MINUTES_IN_DAY;
                        }

                        // safety check: if end time is numerically less/equal to start after parsing without explicit end next day flag, handle overnight shifts if needed
                        if (end <= start && !absence.IsEndNextDay) {
                            end += MINUTES_IN_DAY;
                        }

                        return { start, end };
                    });

                    intervals.sort((a, b) => a.start - b.start);
                    for (let i = 0; i < intervals.length - 1; i++) {
                        if (intervals[i].end > intervals[i + 1].start) {
                            return true;
                        }
                    }

                    return false;
                }

                // there is exactly one absence that day or there is at least one whole day absence linked to social event, we will show only the highest priority one
                if (absences.length === 1 || absences.some(a => isDefined(a.SocialEvent?.Id))) {
                    const absence = getTopPriorityDayAbsence(day) ?? absences[0];
                    const isContinuingAbsence = isDefined(lastAbsenceId) && lastAbsenceId === absence.AbsenceCategory.Id;

                    const partialAbsences = absences.filter(a => !a.IsWholeDay);
                    if (!absence.IsWholeDay && partialAbsences?.length > 1) {
                        // if top priority absence is not whole day, we need to check if there are some other absences that overlap with it, if so we will show conflict
                        const hasConflict = someAbsencesOverlap(partialAbsences);
                        if (hasConflict) {
                            actionsMap[dayIndex].push({
                                color: absence?.AbsenceCategory?.Color as keyof DefaultTheme,
                                title: i18next.t("Attendance:EditAbsence"),
                                isFullDay: true,
                                isStart: true,
                                isEnd: true,
                                categoryId: absence?.AbsenceCategory.Id,
                                hasConflict: true,
                            });
                            return actionsMap;
                        } else {
                            for (const absence of partialAbsences) {
                                actionsMap[dayIndex].push({
                                    color: absence.AbsenceCategory.Color as keyof DefaultTheme,
                                    title: absence.AbsenceCategory.CurrentTemporalPropertyBag?.Name,
                                    isFullDay: absence.IsWholeDay,
                                    categoryId: absence.AbsenceCategory.Id,
                                    isStart: true,
                                    isEnd: true,
                                    hideTitle: true,
                                    order: absence.Order
                                });
                            }
                            return actionsMap;
                        }
                    }

                    actionsMap[dayIndex].push({
                        ...getDayActionFromAbsence(absence),
                        isStart: isMonday || !isContinuingAbsence,
                        isEnd: true,
                        hideTitle: isContinuingAbsence,
                    });


                    if (isContinuingAbsence && actionsMap[dayIndex - 1]) {
                        actionsMap[dayIndex - 1].find(i => !i.isPlaceholder && i.categoryId === absence.AbsenceCategory.Id).isEnd = isMonday;
                    }

                    // everything is handled, we can return the actions map
                    return actionsMap;
                }

                // if there are more absences on the same day, and none linked to social event with whole day absence, we will show a single action with
                // conflict if there is at least one whole day absence, or they overlap, otherwise we will show them all
                const dayHasSomeConflictingAbsence = someAbsencesOverlap(absences);

                if (dayHasSomeConflictingAbsence) {
                    const color = (getTopPriorityDayAbsence(day)?.AbsenceCategory.Color ?? absences[0]?.AbsenceCategory.Color ?? "C_CHART_grey") as keyof DefaultTheme;
                    actionsMap[dayIndex].push({
                        color,
                        title: i18next.t("Attendance:EditAbsence"),
                        isFullDay: true,
                        isStart: true,
                        isEnd: true,
                        categoryId: getTopPriorityDayAbsence(day)?.AbsenceCategory.Id,
                        hasConflict: true,
                    });
                } else {
                    for (const absence of absences) {
                        actionsMap[dayIndex].push({
                            color: absence.AbsenceCategory.Color as keyof DefaultTheme,
                            title: absence.AbsenceCategory.CurrentTemporalPropertyBag?.Name,
                            isFullDay: absence.IsWholeDay,
                            categoryId: absence.AbsenceCategory.Id,
                            isStart: true,
                            isEnd: true,
                            hideTitle: true,
                            order: absence.Order
                        });
                    }
                }
            }
            return actionsMap;
    }, {});
};

export const isAbsenceDisabled: TGetValueFn<boolean> = (args: IGetValueArgs): boolean => {
    const entity = args.item ?? args.storage.getValue(args.bindingContext.getParent());
    // either is linked to social event or is displayed in fast entry list with work intervals
    return !!entity?.SocialEvent?.Id || (args.fastEntryListId === AttendanceFastEntry.Work && !!entity?.AbsenceCategory?.Id);
};

export const isAbsenceTimeDisabled: TGetValueFn<boolean> = (args: IGetValueArgs): boolean => {
    const entity = args.item ?? args.storage.getValue(args.bindingContext.getParent());
    return args.fastEntryListId === AttendanceFastEntry.Work && !!entity?.AbsenceCategory?.Id;
};

function sortIntervalsByTimeStart(i1: IPrAttendanceDayIntervalEntity, i2: IPrAttendanceDayIntervalEntity): number {
    if (i1.IsStartNextDay && !i2.IsStartNextDay) {
        return 1;
    }
    if (!i1.IsStartNextDay && i2.IsStartNextDay) {
        return -1;
    }
    return (getUtcDayjs(i1.TimeStart).get("hours") * 60 + getUtcDayjs(i1.TimeStart).get("minutes")) - (getUtcDayjs(i2.TimeStart).get("hours") * 60 + getUtcDayjs(i2.TimeStart).get("minutes"));
}

export function getSortedIntervals(entity: IPrAttendanceDayEntity): IPrAttendanceDayIntervalEntity[] {
    return [...(entity?.Intervals ?? [])].sort((a, b) => a.Order - b.Order);
}

export const getWorkIntervals = (entity: IPrAttendanceDayEntity): IPrAttendanceDayIntervalEntity[] => {
    return entity?.Intervals?.filter(i => isValidInterval(i) && !i.AbsenceCategory?.Id && !i.IsPlaceholder) ?? [];
};

export const getAbsenceIntervals = (entity: IPrAttendanceDayEntity, ignoreInvalid = false): IPrAttendanceDayIntervalEntity[] => {
    return entity?.Intervals?.filter(i => (ignoreInvalid || isValidInterval(i)) && !!i.AbsenceCategory?.Id && !i.IsPlaceholder) ?? [];
};

export const getPlaceholders = (entity: IPrAttendanceDayEntity): IPrAttendanceDayIntervalEntity[] => {
    return entity?.Intervals?.filter(i => isValidInterval(i) && !!i.AbsenceCategory?.Id && !!i.IsPlaceholder) ?? [];
};

export const transformDateToTodayTime = (date: Date): Dayjs => {
    const defaultStart = getUtcDayjs(date);
    return getUtcDayjs().startOf("day").add(defaultStart.get("hours"), "hours").add(defaultStart.get("minutes"), "minutes");
};

function findMatchingAbsence(
    absenceIntervals: IPrAttendanceDayIntervalEntity[],
    startDate: Dayjs,
    endDate: Dayjs
): IPrAttendanceDayIntervalEntity | null {
    if (!Array.isArray(absenceIntervals) || !startDate?.isValid() || !endDate?.isValid()) {
        return null;
    }

    return absenceIntervals.filter((interval) => !interval?.IsWholeDay).find((interval) => {
        if (!interval || !interval.TimeStart || !interval.TimeEnd) {
            return false;
        }

        const intervalStart = transformDateToTodayTime(interval.TimeStart);
        const intervalEnd = transformDateToTodayTime(interval.TimeEnd);

        // if start and end are same it means that interval is not defined properly and we should skip it
        if (intervalStart.diff(intervalEnd, "m") === 0) {
            return false;
        }

        return startDate.isBefore(intervalEnd, "minute") &&
            endDate.isAfter(intervalStart, "minute");
    });
}

function getNewInterval(storage: FormStorage, startDate: Dayjs, endDate: Dayjs, lastWasBreak: boolean, maxId: number): IPrAttendanceDayIntervalEntity {
    const newInterval: IPrAttendanceDayIntervalEntity = addEmptyLineItem<IPrAttendanceDayIntervalEntity>({
        storage,
        bindingContext: storage.data.bindingContext.navigate(PrAttendanceEntity.Days).navigate(PrAttendanceDayEntity.Intervals),
        newItemsCount: maxId + 1,
        columns: [{
            id: PrAttendanceDayIntervalEntity.TimeStart
        }, {
            id: PrAttendanceDayIntervalEntity.TimeEnd
        }, {
            id: timeDiffPath
        }],
        context: storage.context
    });

    return {
        ...newInterval,
        TimeStart: startDate.clone().toDate(),
        TimeEnd: endDate.toDate(),
        IsStartNextDay: !getUtcDayjs().isSame(startDate, "day"),
        IsEndNextDay: !getUtcDayjs().isSame(endDate, "day"),
        WorkType: {
            Code: lastWasBreak ? PrWorkIntervalTypeCode.Work : PrWorkIntervalTypeCode.MealBreak
        }
    };
}

export function getAllAbsenceTime(absenceIntervals: IPrAttendanceDayIntervalEntity[]): number {
    return absenceIntervals.reduce((total: number, interval) => {
        if (!interval.IsWholeDay) {
            if (isNotDefined(interval.TimeStart) || isNotDefined(interval.TimeEnd)) {
                return total;
            }
            const diff = getHourDifference(getUtcDayjs(interval.TimeStart), getUtcDayjs(interval.TimeEnd), true, true);
            return total + diff;
        } else {
            // not sure about this, theoretically day fund might be more than 24 hours
            return 60 * 24; // assuming whole day absence means 24 hours of absence, adjust if needed
        }
    }, 0);
}

export const generateIntervalsForDay = (dayBc: BindingContext, minutes: number, storage: FormStorage, dontGenerateHolidayCompensation = false): void => {
    const workingPattern = getWorkingPattern(storage.data.entity);
    const absenceIntervals = getAbsenceIntervals(storage.getValue(dayBc));
    const placeholderIntervals = getPlaceholders(storage.getValue(dayBc));
    const defaultStart = getUtcDayjs(storage.getValue(dayBc.navigate(DayWorkStartPath)));

    const customData = storage.getCustomData() as IAttendanceCustomData;
    const payrollSettings = customData.payrollSettings;
    const isHoliday = customData.holidays?.some(h => isSameDay(h, storage.getValue(dayBc).Date));
    const isDayBeforeHoliday = customData.holidays?.some(h => getUtcDayjs(storage.getValue(dayBc).Date).add(1, "day").isSame(h, "day"));
    const holidayShiftCode = payrollSettings.CurrentTemporalPropertyBag.HolidayShiftAssignmentCode;
    const autoGenerateHolidayCompensation = (isHoliday || isDayBeforeHoliday) && !workingPattern.IsIncludingHolidays && !dontGenerateHolidayCompensation;

    let startDate = getUtcDayjs().startOf("day").add(defaultStart.get("hours"), "hours").add(defaultStart.get("minutes"), "minutes");

    let minutesLeft = minutes;
    let lastWasBreak = true;
    const newIntervals = [];

    const loopControlLimit = 100; // to prevent potential infinite loop, adjust as necessary

    let maxId = getNewItemsMaxId(absenceIntervals);

    while (minutesLeft > 0 && newIntervals.length < loopControlLimit) {
        const nextIntervalMaxLength = (lastWasBreak ? WORK_LENGTH * 60 : BREAK_LENGTH * 60);
        let endDate = startDate.add(Math.min(minutesLeft, nextIntervalMaxLength), "minutes");

        const matchingAbsence = findMatchingAbsence(absenceIntervals, startDate, endDate);

        if (!isObjectEmpty(matchingAbsence)) {
            // where the work is conflicting with some absence, we need to skip it, subtract the time from minutesLeft and move startDate to the end of absence
            if (transformDateToTodayTime(matchingAbsence.TimeStart).isAfter(startDate)) {
                endDate = transformDateToTodayTime(matchingAbsence.TimeStart);
            } else if (transformDateToTodayTime(matchingAbsence.TimeStart).isSameOrBefore(startDate)) {
                startDate = transformDateToTodayTime(matchingAbsence.TimeEnd);
                lastWasBreak = true;
                continue;
            }
        }

        if ((autoGenerateHolidayCompensation && PrHolidayShiftAssignmentCode.SplitByMidnight === holidayShiftCode) &&
            !startDate.isSame(endDate, "day") && lastWasBreak) {
            // if the shift is ending on holiday, we need to split the interval to end at midnight, so we can mark the work as compensation for holiday
            const midnight = endDate.startOf("day");
            const newInterval = getNewInterval(storage, startDate, midnight, lastWasBreak, maxId);
            maxId++;
            newIntervals.push(newInterval);
            minutesLeft -= midnight.diff(startDate, "minutes");

            startDate = midnight.clone();
        }

        const newInterval = getNewInterval(storage, startDate, endDate, lastWasBreak, maxId);
        maxId++;

        if (lastWasBreak) {
            minutesLeft -= endDate.diff(startDate, "minutes");
        }

        startDate = endDate.clone();
        newIntervals.push(newInterval);
        lastWasBreak = !lastWasBreak;
    }

    // make holiday adjustments if necessary
    if (autoGenerateHolidayCompensation) {
        const workIntervals = newIntervals.filter(interval => interval.WorkType?.Code === PrWorkIntervalTypeCode.Work);
        const holidayTypeExpanded = getExpandedEnumProps(EntityTypeName.PrWorkIntervalType, PrWorkIntervalTypeCode.CompensationForHoliday, PrAttendanceDayIntervalEntity.WorkType);
        switch (holidayShiftCode) {
            case PrHolidayShiftAssignmentCode.SplitByMidnight:
                workIntervals.forEach(interval => {
                    const isCompensation = (isHoliday && !interval.IsStartNextDay) || (isDayBeforeHoliday && interval.IsStartNextDay);
                    if (isCompensation) {
                        Object.assign(interval, holidayTypeExpanded);
                    }
                });
                break;
            case PrHolidayShiftAssignmentCode.ByShiftEnd:
                if (isDayBeforeHoliday && workIntervals.some(interval => interval.IsEndNextDay)) {
                    workIntervals.forEach(interval => {
                        Object.assign(interval, holidayTypeExpanded);
                    });
                }
                break;
            case PrHolidayShiftAssignmentCode.ByShiftStart:
                workIntervals.forEach(interval => {
                    if (isHoliday) {
                        Object.assign(interval, holidayTypeExpanded);
                    }
                });
                break;
        }
    }

    storage.setValue(dayBc.navigate(PrAttendanceDayEntity.Intervals), [...newIntervals, ...absenceIntervals, ...placeholderIntervals].sort(sortIntervalsByTimeStart).map((interval, index) => {
        interval.Order = index + 1;
        return interval;
    }));
};

export const isDayHoliday = (day: IPrAttendanceDayEntity, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): boolean => {
    const holidays = storage.getCustomData().holidays;
    return !!holidays?.some(h => isSameDay(h, day.Date));
};

export const getDaySaldo = (day: IPrAttendanceDayEntity, fund: number, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): number => {
    let datedMinutes: number;
    if (day.Intervals?.some(i => i.IsWholeDay && !i.IsPlaceholder)) {
        datedMinutes = fund;
    } else {
        datedMinutes = getDayIntervalsMinutes(day, IntervalType.All);
    }
    return datedMinutes - fund;
};

export const updateDaySaldo = (day: IPrAttendanceDayEntity, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): void => {
    if (day) {
        const dayIndex = getUtcDayjs(day.Date).get("date");
        const fund = getDayFund(dayIndex, storage);

        storage.setCustomData({
            saldoMap: {
                ...storage.getCustomData().saldoMap,
                [dayIndex]: getDaySaldo(day, fund, storage)
            }
        });
    }
};

export function updateWorkedHoursMap(day: IPrAttendanceDayEntity, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): void {
    if (day) {
        const dayIndex = getUtcDayjs(day.Date).get("date");

        storage.setCustomData({
            workedHoursMap: {
                ...storage.getCustomData().workedHoursMap,
                [dayIndex]: getDayIntervalsMinutes(day, IntervalType.Work)
            }
        });
    }
}

export const createNewAttendanceDayEntity = (dayIndex: number, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): IPrAttendanceDayEntity => {
    const days = storage.data.entity.Days;
    const newItemsMax = getNewItemsMaxId(days);
    let newDay = addEmptyLineItem<IPrAttendanceDayEntity>({
        storage,
        bindingContext: storage.data.bindingContext.navigate(PrAttendanceEntity.Days),
        newItemsCount: newItemsMax + 1,
        columns: [],
        context: storage.context
    });

    newDay = {
        ...newDay,
        Date: createDateFromPeriod(storage.data.entity, dayIndex),
        IsOnRegistry: true,
        Intervals: []
    };

    days.push(newDay);
    return newDay;
};


export const refreshActions = (storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): void => {
    const actions = parseDayActionsFromData(storage.data.entity.Days);
    storage.setCustomData({ actions });
};

export const refreshBalance = (storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): void => {
    const saldoMap = storage.getCustomData().saldoMap;
    const lastTimeOvertimeWork = storage.getCustomData().lastTimeOvertimeWork ?? 0;

    const balance = Object.values(saldoMap).reduce((balance, saldo) => {
        return balance + saldo;
    }, 0);

    storage.setValueByPath(PrAttendanceEntity.Balance, balance + lastTimeOvertimeWork);
};

export const refreshSaldoMap = (storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>, days?: Set<number>): void => {
    const selectedDays = days ?? storage.getCustomData().selectedDays;
    selectedDays.forEach(day => {
        updateDaySaldo(getDayByIndex(storage.data.entity, day), storage);
    });
};

export function refreshWorkedHoursMap(storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>, days?: Set<number>): void {
    const selectedDays = days ?? storage.getCustomData().selectedDays;
    selectedDays.forEach(day => {
        updateWorkedHoursMap(getDayByIndex(storage.data.entity, day), storage);
    });
}

type TWorkInterval = (IPrAttendanceDayIntervalEntity | IPrWorkingPatternDayIntervalEntity);

export function transformWorkIntervalsBeforeSave(intervals: TWorkInterval[], onlyClean = false): TWorkInterval[] {
    const sortedIntervals: IPrAttendanceDayIntervalEntity[] = intervals?.filter(interval => {
        return "AbsenceCategory" in interval ? interval.AbsenceCategory?.Id !== ATTENDANCE_FAKE_ID : true;
    })?.sort((a, b) => a.Order - b.Order);

    if (!sortedIntervals?.length) {
        return [];
    }

    let highestHour: Date = null;
    let isNextDay = false;

    return sortedIntervals.map((interval, index) => {
        interval.Order = index + 1; // BE starts ordering with 1
        if (!onlyClean) {
            if (!isNextDay && (!highestHour || getHourDifference(getUtcDayjs(highestHour), getUtcDayjs(interval.TimeStart), false) >= 0)) {
                highestHour = interval.TimeStart;
                interval.IsStartNextDay = false;
            } else {
                isNextDay = true;
                interval.IsStartNextDay = true;
            }

            if (!isNextDay && getHourDifference(getUtcDayjs(highestHour), getUtcDayjs(interval.TimeEnd), false) >= 0) {
                highestHour = interval.TimeEnd;
                interval.IsEndNextDay = false;
            } else {
                isNextDay = true;
                interval.IsEndNextDay = true;
            }
        }

        if (!isObjectEmpty(interval.AbsenceCategory)) {
            interval.WorkTypeCode = null;
            interval.WorkType = null;
        }

        if (isDefined(interval.WorkType)) {
            interval.AbsenceCategory = {};
        }

        return interval;
    });
}

export const getPublicHolidays = memoize(async (country: CountryCode, year: number): Promise<Dayjs[]> => {
    const url = `${REST_API_URL}/PrCalendar/PublicHolidays/${country}/${year}`;
    const res = await customFetch(url);
    const data = await res.json();
    return data.map((d: string) => DateType.isValid(d) ? getUtcDayjs(d) : null).filter((d: unknown) => !!d);
}, (country, year) => [country, year].join("-"));

export const getMonthHolidays = memoize(async (monthDate: Dayjs, year: number, countryCode: CountryCode): Promise<Date[]> => {
    const cc = countryCode ?? CountryCode.CzechRepublic;
    return (await getPublicHolidays(cc, year))
        .filter(holiday => monthDate.isSame(holiday, "month"))
        .map(holiday => holiday.toDate());
});

interface IPrEntityWithDaysIntervals {
    Days?: ({
        Date?: Date;
        Intervals?: ({ Id?: number, IsWholeDay?: boolean })[]
    })[];
}

export const pairIntervalsWithExistingIds = <T extends IPrEntityWithDaysIntervals>(storage: FormStorage, entity: T): IPrEntityWithDaysIntervals => {
    if (!storage.data.bindingContext.isNew()) {
        for (const day of entity.Days) {
            const origDay = (storage.data.origEntity as IPrEntityWithDaysIntervals).Days?.find(d => isSameDay(getUtcDayjs(d.Date), getUtcDayjs(day.Date)));
            if (isObjectEmpty(origDay)) {
                continue;
            }
            let origIntervals: IPrAttendanceDayIntervalEntity[] = [...origDay.Intervals];
            day.Intervals = day.Intervals.map(interval => {
                const firstInterval = origIntervals?.find(i => i.IsWholeDay === interval.IsWholeDay && i.IsPlaceholder === false);
                if (isNotDefined(interval.Id) && firstInterval) {
                    origIntervals = origIntervals.filter(i => i.Id !== firstInterval.Id);
                    return {
                        ...interval,
                        Id: Number(firstInterval.Id)
                    };
                }
                return interval;
            });
        }
    }
    return entity;
};

export function getDayFund(dayIndex: number, storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): number {
    const day = getDayByIndex(storage.data.entity, dayIndex);
    return isNaN(Number(day?.Planned)) ? 0 : day?.Planned;
}

// parser for fields that store time as minutes in backend and use time picker as input
export const timeStampToMinutesParser = (val: string): number => {
    const date = DateType.parse({
        date: parseTimeUserInput(val),
        format: formatTimeVariants(longDateFormat(getDateFormat(DateType.defaultTimeFormat))),
        strictMode: false
    });
    if (!DateType.isValid(date)) {
        return null;
    }

    const utcDate = getUtcDayjs(date);
    return utcDate.get("hour") * 60 + utcDate.get("minute");
};

// formatter for fields that store time as minutes in backend and use time picker as input
export const timeStampToMinutesFormatter = (val: TValue): string => {
    let minutes = Number(val);
    if (!isDefined(val) || isNaN(minutes)) {
        minutes = 0;
    }
    const date = getUtcDayjs().set("hours", Math.floor(minutes / 60)).set("minutes", minutes % 60).toDate();
    return DateType.format(date, longDateFormat(getDateFormat(DateType.defaultTimeFormat)));
};

export async function clearAttendance(attendanceId: number): Promise<void> {
    const url = `${CLEAR_ATTENDANCE_URL}${attendanceId}`;
    const res = await customFetch(url, {
        method: "POST"
    });

    if (!res.ok) {
        throw new Error(`Failed to clear attendance with id ${attendanceId}`);
    }
}

const MINIMAL_MINUTES_WORKED_FOR_MEAL_VOUCHER = 3 * 60;
const MINIMAL_MINUTES_WORKED_FOR_SECOND_MEAL_VOUCHER = 11 * 60;

function getNumberOfMealVouchersForDay(workedMinutes: number, minimalMinutesForMealVoucher: number): number {
    if (!isDefined(minimalMinutesForMealVoucher)) {
        logger.warn("Minimal minutes for meal voucher is not defined, using default values");
        minimalMinutesForMealVoucher = MINIMAL_MINUTES_WORKED_FOR_MEAL_VOUCHER;
    }
    if (workedMinutes < minimalMinutesForMealVoucher) {
        return 0;
    }
    if (workedMinutes < MINIMAL_MINUTES_WORKED_FOR_SECOND_MEAL_VOUCHER) {
        return 1;
    }
    return 2;
}

export function hasMealVoucherBenefit(entity: IPrAttendanceEntity, monthDate: Dayjs): boolean {
    const monthStart = monthDate.startOf("month");
    const monthEnd = monthDate.endOf("month");

    return !!entity.Employment?.Benefits?.some(benefit => {
        if (benefit.TypeCode !== PrBenefitTypeCode.MealVouchers) {
            return false;
        }
        const validFrom = getUtcDayjs(benefit.DateValidFrom);
        const validTo = isDefined(benefit.DateValidTo) ? getUtcDayjs(benefit.DateValidTo) : null;
        return !validFrom.isAfter(monthEnd) && (!validTo || !validTo.isBefore(monthStart));
    });
}

interface IAbsenceCategoryTotal {
    id: number;
    name: string;
    minutes: number;
}

export function getMonthlyAbsenceCategoryTotals(entity: IPrAttendanceEntity): IAbsenceCategoryTotal[] {
    const totals = new Map<number, IAbsenceCategoryTotal>();

    for (const day of entity.Days ?? []) {
        for (const interval of getAbsenceIntervals(day)) {
            const minutes = getIntervalMinutes(day, interval);
            if (!minutes) {
                continue;
            }
            const category = interval.AbsenceCategory;
            const existing = totals.get(category.Id);
            totals.set(category.Id, {
                id: category.Id,
                name: existing?.name || category.CurrentTemporalPropertyBag?.Name || "",
                minutes: (existing?.minutes ?? 0) + minutes
            });
        }
    }

    return Array.from(totals.values());
}

const mealVoucherFormatter = (value: number): string => `${value} ${i18next.t("Common:General.PiecesUnit")}`;

function formatDppLimit(value: number): string {
    return i18next.t("Attendance:Form.DPPLimitValue", {
        value: formatMinutesToHoursString(value),
        limit: formatMinutesToHoursString(DPP_YEARLY_LIMIT_IN_MINUTES)
    }); // todo: make the limit configurable
}

export function getMonthlyOverviewFondSections(storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): IFormTableListSection[] {
    const entity = storage.data.entity;
    const days = entity.Days ?? [];

    const monthDate = getUtcDayjs().set("month", entity.Month - 1).set("year", entity.Year);

    const totalFundMinutes = days.reduce((acc, day) => {
        return acc + (day.Planned ?? 0);
    }, 0);


    const totalWorkedMinutes = days.reduce((sum, day) => sum + getDayIntervalsMinutes(day, IntervalType.Work), 0);
    const workedMinutesThisYear = storage.getCustomData().workedMinutesThisYear ?? 0;
    const holidayWorkMinutes = days
        .filter(day => isDayHoliday(day, storage))
        .reduce((sum, day) => sum + getDayIntervalsMinutes(day, IntervalType.Work), 0);

    const minimalWorkedMinutesForMealVoucher = storage.getCustomData().payrollSettings?.TemporalPropertyBag?.find(b => {
        return (!b.DateValidFrom || getUtcDayjs(b.DateValidFrom).isSameOrBefore(monthDate)) && (!b.DateValidTo || getUtcDayjs(b.DateValidTo).isSameOrAfter(monthDate));
    }).MinimalMinutesForMealVoucher;
    const mealVoucherCount = hasMealVoucherBenefit(entity, monthDate)
        ? days.reduce((sum, day) => sum + getNumberOfMealVouchersForDay(getDayIntervalsMinutes(day, IntervalType.Work), minimalWorkedMinutesForMealVoucher), 0)
        : 0;

    const isDpp = entity.Employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPP;
    const isDpc = entity.Employment.TypeCode === PrEmploymentTypeCode.WorkAgreementDPC;


    return [{
        rows: [
            { title: i18next.t("Attendance:Form.WorkingHoursFund"), value: totalFundMinutes, formatter: formatMinutesToHoursString },
            { title: i18next.t("Attendance:Form.TotalWorked"), value: totalWorkedMinutes, formatter: formatMinutesToHoursString },
            ...(isDpp ? [{
                title: i18next.t("Attendance:Form.DPPLimit"),
                value: totalWorkedMinutes + workedMinutesThisYear,
                formatter: formatDppLimit
            }] : []),
            ...(!isDpc && !isDpp ? [{
                title: i18next.t("Attendance:Form.OverFund"),
                value: Math.max(0, totalWorkedMinutes - totalFundMinutes),
                formatter: formatMinutesToHoursString
            }] : []),
            { title: i18next.t("Attendance:Form.HolidayWork"), value: holidayWorkMinutes, formatter: formatMinutesToHoursString },
            !!mealVoucherCount && {
                title: i18next.t("Attendance:Form.MealVoucherEntitlement"),
                value: mealVoucherCount,
                formatter: mealVoucherFormatter
            }
        ].filter(Boolean) as IFormTableListRow[]
    }];
}

export function getMonthlyOverviewAbsenceSections(storage: FormStorage<IPrAttendanceEntity, IAttendanceCustomData>): IFormTableListSection[] {
    const entity = storage.data.entity;
    const days = entity.Days ?? [];

    const totalAbsenceMinutes = days.reduce((sum, day) => sum + getDayIntervalsMinutes(day, IntervalType.Absence), 0);

    const categoryRows: IFormTableListRow[] = getMonthlyAbsenceCategoryTotals(entity).map(category => ({
        title: category.name,
        value: category.minutes,
        formatter: formatMinutesToHoursString,
        isSubRow: true
    }));

    return [{
        rows: [
            { title: i18next.t("Attendance:Form.Absence"), value: totalAbsenceMinutes, formatter: formatMinutesToHoursString },
            ...categoryRows
        ]
    }];
}

export function hasWholeDaySocialEvent(day: IPrAttendanceDayEntity): boolean {
    return day.Intervals?.some(i => !!i.AbsenceCategory?.Id && !!i.IsWholeDay && !i.IsPlaceholder && isDefined(i.SocialEvent?.Id));
}

export const GENERAL_ABSENCE_CATEGORY_GROUP_ID = "general-absence-category-group";
export const SOCIAL_EVENT_CATEGORY_GROUP_ID = "social-event-category-group";

// just for sorting to group in combobox
export const SocialEventCategoryTypes: PrAbsenceCategoryTypeCode[] = [
    PrAbsenceCategoryTypeCode.DPN,
    PrAbsenceCategoryTypeCode.Ošetřovné,
    PrAbsenceCategoryTypeCode.DlouhodobéOšetřovné,
    PrAbsenceCategoryTypeCode.MateřskáDovolená,
    PrAbsenceCategoryTypeCode.OtcovskáDovolená,
    PrAbsenceCategoryTypeCode.RodičovskáDovolená,
    PrAbsenceCategoryTypeCode.Vazba,
    PrAbsenceCategoryTypeCode.VýkonTrestu
];

export function renderLinkToSocialEvent(absenceInterval: IPrAttendanceDayIntervalEntity, args: IFieldDefFn): React.ReactNode {
    if (!absenceInterval?.SocialEvent?.Id) {
        return null;
    }
    const linkText = `${absenceInterval.AbsenceCategory?.CurrentTemporalPropertyBag?.Name} (${absenceInterval.SocialEvent?.NumberOurs})`;
    return <Field
        name={args.props.name}
        label={args.props.info.label}
        style={{ fontSize: "14px" }}
        isReadOnly>
        {getIntentLink(linkText, {
            route: `${ROUTE_SOCIAL_EVENTS}/${getEntitySetFromType(absenceInterval.SocialEvent.SocialEventTypeCode)}/${absenceInterval.SocialEvent.Id}`,
            context: args.storage.context,
            storage: args.storage
        })}
    </Field>;
}

export function isDayIntervalTimeVisible(args: IGetValueArgs): boolean {
    const dayBc = args.bindingContext.getParentCollection().getParent();
    const isWholeDayAbsence = args.storage.getValue(dayBc.navigate(IsFullDayAbsencePath)) as boolean;
    return !isWholeDayAbsence || (args.fastEntryListId !== AttendanceFastEntry.Absence);
}

export function getDayByIndex(entity: IPrAttendanceEntity, dayIndex: number): IPrAttendanceDayEntity {
    return entity.Days.find(d => getUtcDayjs(d.Date).get("date") === dayIndex);
}

export const fetchPayrollSettingsForAttendance = memoizeOne(async (storage: FormStorage): Promise<IPrPayrollSettingEntity> => {
    const { oData } = storage;
    const query = oData.getEntitySetWrapper(EntitySetName.PrPayrollSettings).query()
        .expand(PrPayrollSettingEntity.CurrentTemporalPropertyBag, (q) => {
            q.select(
                PrPayrollSettingTemporalCurrentEntity.HolidayShiftAssignmentCode,
                PrPayrollSettingTemporalCurrentEntity.MinimalMinutesForMealVoucher
            );
        }).expand(PrPayrollSettingEntity.TemporalPropertyBag, (q) => {
            q.select(
                PrPayrollSettingTemporalEntity.HolidayShiftAssignmentCode,
                PrPayrollSettingTemporalEntity.MinimalMinutesForMealVoucher,
                PrPayrollSettingTemporalEntity.DateValidTo,
                PrPayrollSettingTemporalEntity.DateValidFrom
            );
        });

    const res = await query.fetchData<IPrPayrollSettingEntity[]>();
    if (res.value?.length !== 1) {
        logger.error("There should be only one PrPayrollSettings entity");
    }
    const payrollSetting = res.value?.[0];
    if (!payrollSetting) {
        logger.error("PrPayrollSettings entity not found");
    }
    return payrollSetting;
}, () => [true/* cache forever */]);

export function getFilteredAbsencesForDay(items: IPrAttendanceDayIntervalEntity[], selectedDay: IPrAttendanceDayEntity): IPrAttendanceDayIntervalEntity[] {
    // filter out placeholders and lower priority absences if there is a whole day absence or social event linked to absence
    return (items as IPrAttendanceDayIntervalEntity[]).filter(i => {
        let res = !i.IsPlaceholder;
        const otherItems = items.filter(item => item !== i && !item.IsPlaceholder);
        if (i.IsWholeDay) {
            res = res && !otherItems.some((item: IPrAttendanceDayIntervalEntity) => i.AbsenceCategory?.Type?.Priority > item.AbsenceCategory?.Type?.Priority);
        } else if (!isObjectEmpty(i.SocialEvent)) {
            res = res && !otherItems.some((item: IPrAttendanceDayIntervalEntity) => item.IsWholeDay && i.AbsenceCategory?.Type?.Priority > item.AbsenceCategory?.Type?.Priority);
        } else if (!isObjectEmpty(i.AbsenceCategory)) {
            // partial day absences
            res = res && !getTopPriorityDayAbsence(selectedDay)?.IsWholeDay;
        }
        return res;
    });
}